import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Popper, Paper, Typography, IconButton, keyframes } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
import { AppItem } from "./App";
import { useState } from "react";

  const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-3deg); }
  75% { transform: rotate(3deg); }
`;


type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onOpenToolsDrawer?: () => void;
  onReorder: (apps: AppItem[]) => void;
  appList: AppItem[];
  placement?: "bottom" | "bottom-start" | "bottom-end" | "top" | "top-start" | "top-end";
  offset?: [number, number];
};

interface SortableAppItemProps {
  app: AppItem;
  onAppClick: (app: AppItem) => void;
  isEditMode: boolean;
}

const SortableAppItem = ({
  app,
  onAppClick,
  isEditMode,
}: SortableAppItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.label });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = app.icon;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...(isEditMode ? attributes : {})}
      {...(isEditMode ? listeners : {})}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 3,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: isDragging || !isEditMode ? "none" : "translateY(-4px)",
        },
      }}
    >
      <Box
        onClick={(e) => {
          if (!isDragging && !isEditMode) {
            e.stopPropagation();
            onAppClick(app);
          }
        }}
        sx={{
          width: 46,
          height: 46,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: app.color,
          cursor: isEditMode ? (isDragging ? "grabbing" : "grab") : "pointer",
          animation:
            isEditMode && !isDragging
              ? `${shake} 0.3s ease-in-out infinite`
              : "none",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: isEditMode ? "none" : `0 6px 16px ${app.color}60`,
          },
        }}
      >
        <IconComponent sx={{ fontSize: 32, color: "white" }} />
      </Box>
      <Typography
        variant="body2"
        fontWeight="500"
        textAlign="center"
        sx={{
          fontSize: "0.75rem",
          lineHeight: 1.1,
        }}
      >
        {app.label}
      </Typography>
    </Box>
  );
};

const AppLauncher = ({
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onOpenToolsDrawer,
  onReorder,
  appList,
  placement,
  offset,
}: Props) => {
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = appList.findIndex((app) => app.label === active.id);
      const newIndex = appList.findIndex((app) => app.label === over.id);
      const newOrder = arrayMove(appList, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  const handleAppClick = (app: AppItem) => {
    if (app.action === "OPEN_TOOLS_DRAWER") {
      onOpenToolsDrawer?.();
      onClose();
      return;
    }

    if (app.route) {
      navigate(app.route);
      onClose();
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement || "bottom"}
      modifiers={[{ name: "offset", options: { offset: offset || [0, 8] } }]}
      sx={{ zIndex: 1300 }}
    >
      <Paper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        elevation={8}
        sx={{
          width: 320,
          px: 2,
          py: 2,
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Apps
          </Typography>
          <IconButton
            onClick={toggleEditMode}
            size="small"
            sx={{
              backgroundColor:  "transparent",
              color: "text.primary",
              "&:hover": {
                backgroundColor: "action.selected",
              },
            }}
          >
            {isEditMode ? <CheckIcon /> : <EditIcon />}
          </IconButton>
        </Box>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={appList.map((app) => app.label)}
            strategy={rectSortingStrategy}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1.5,
              }}
            >
              {appList.map((app) => (
                <SortableAppItem
                  key={app.label}
                  app={app}
                  onAppClick={handleAppClick}
                  isEditMode={isEditMode}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Paper>
    </Popper>
  );
};

export default AppLauncher;
